import { Stack } from 'common/ui/Layout';
import { Button } from 'common/ui/Button';
import Dialog from 'common/ui/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Refresh from '@mui/icons-material/Refresh';
import type { ValidationResult } from './useSystemCheck';
import { useMemo } from 'react';
import Tooltip from 'common/ui/Tooltip';
import {
  SysChkContainer,
  SysChkHeader,
  SysChkHeaderVariant,
  SysChkItem,
  SysChkProgress,
} from './SystemCheckUI';
import { SysChkValidator, SysChkValidatorEnforcement, SystemCheckValidators } from './validators';

const BYPASS_TOOLTIP_CONTENT =
  'Bypassing the system check will allow you to continue without resolving the issues. Please proceed with caution.';

const VALIDATION_IN_PROGRESS_TOOLTIP_CONTENT = 'Validation is in progress, please wait...';

const getSysCheckStyleAttr = (
  isSystemValid: boolean,
  shouldBypassSystemCheck: boolean,
  isValidationInProgress: boolean,
  hasWarnings: boolean
) => {
  if (isValidationInProgress) {
    return {
      systemCheckText: 'Checking system...',
      variant: SysChkHeaderVariant.PROGRESS,
    };
  } else if (shouldBypassSystemCheck && isSystemValid) {
    return {
      systemCheckText: 'System check passed and bypassed',
      variant: SysChkHeaderVariant.BYPASS_PASS,
    };
  } else if (shouldBypassSystemCheck && !isSystemValid) {
    return {
      systemCheckText: 'System check failed but bypassed',
      variant: SysChkHeaderVariant.BYPASS_FAIL,
    };
  } else if (isSystemValid && hasWarnings) {
    return {
      systemCheckText: 'System check passed with warnings',
      variant: SysChkHeaderVariant.WARN,
    };
  } else if (isSystemValid) {
    return {
      systemCheckText: 'System check passed!',
      variant: SysChkHeaderVariant.PASS,
    };
  }

  return {
    systemCheckText: 'System check failed',
    variant: SysChkHeaderVariant.FAIL,
  };
};

const renderGpuDetails = (metadata: ValidationResult['metadata']): React.ReactNode => {
  const renderer = (metadata?.renderer as string | undefined) ?? 'Unavailable';
  const vendor = (metadata?.vendor as string | undefined) ?? 'Unavailable';
  const glVersion = (metadata?.glVersion as string | undefined) ?? 'Unavailable';

  return (
    <dl
      css={`
        margin: 0;
        display: grid;
        grid-template-columns: max-content 1fr;
        column-gap: 0.75rem;
        row-gap: 0.25rem;
        & > dt {
          font-weight: 600;
          color: rgba(255, 255, 255, 0.6);
        }
        & > dd {
          margin: 0;
          word-break: break-word;
        }
      `}
      data-testid="syschk-gpu-details"
    >
      <dt>Renderer</dt>
      <dd>{renderer}</dd>
      <dt>Vendor</dt>
      <dd>{vendor}</dd>
      <dt>GL Version</dt>
      <dd>{glVersion}</dd>
    </dl>
  );
};

const RefreshNotice = () => (
  <div
    css={`
      font-size: 12px;
      line-height: 1.4;
      color: rgba(255, 255, 255, 0.7);
      text-align: right;
    `}
  >
    If you've recently enabled or installed the extension, you must refresh this window to get
    accurate results.
  </div>
);

export type Props = {
  isSystemValid: boolean;
  validationResults: Array<ValidationResult>;
  isOpen: boolean;
  shouldBypassSystemCheck: boolean;
  recheck: () => void;
  setBypass: (shouldBypass: boolean) => void;
  setModalState: (arg1: boolean) => void;
};
export const SystemCheckModal = ({
  isSystemValid,
  isOpen,
  shouldBypassSystemCheck,
  validationResults,
  setBypass,
  setModalState,
  recheck,
}: Props): React.ReactElement => {
  const isValidationInProgress = validationResults.length === 0;

  // Recommended validators (HardwareAcceleration, GpuCapability) don't block
  // the check but surface as warnings when they fail.
  const hasWarnings = useMemo(
    () =>
      validationResults.some(
        (r) =>
          r.result === false &&
          SystemCheckValidators.get(r.id)?.enforcement === SysChkValidatorEnforcement.Recommended
      ),
    [validationResults]
  );

  // While validation is still running there are no failing results yet, so
  // setBypass would be a silent no-op — keep the button disabled until results land.
  const isBypassDisabled = isValidationInProgress;

  const { systemCheckText, variant } = useMemo(
    () =>
      getSysCheckStyleAttr(
        isSystemValid,
        shouldBypassSystemCheck,
        isValidationInProgress,
        hasWarnings
      ),
    [isSystemValid, shouldBypassSystemCheck, isValidationInProgress, hasWarnings]
  );

  const handleBypassClick = () => {
    setBypass(!shouldBypassSystemCheck);
  };

  return (
    <Dialog open={isOpen} maxWidth="xl" data-testid="dialog-open" sx={{ minHeight: 400 }}>
      <DialogTitle>Sirona System Check</DialogTitle>
      <DialogContent style={{ paddingBottom: '1rem' }}>
        <SysChkContainer>
          <SysChkHeader variant={variant}>{systemCheckText}</SysChkHeader>

          {validationResults.length === 0 && <SysChkProgress />}
          {validationResults.map((validationResult) => {
            const isGpuRow = validationResult.id === SysChkValidator.GpuCapability;
            const isWarning =
              !validationResult.result &&
              SystemCheckValidators.get(validationResult.id)?.enforcement ===
                SysChkValidatorEnforcement.Recommended;
            return (
              <SysChkItem
                id={validationResult.id}
                result={validationResult.result}
                key={validationResult.id}
                title={validationResult.reason ?? ''}
                isWarning={isWarning}
                expandableContent={
                  isGpuRow ? renderGpuDetails(validationResult.metadata) : undefined
                }
              />
            );
          })}

          <Stack
            css={`
              margin: 2rem 0 0.25rem;
              gap: 1.5rem;
              align-items: center;
              justify-content: center;
            `}
          >
            {isSystemValid === false && !isValidationInProgress && <RefreshNotice />}
            <Button
              disabled={isValidationInProgress}
              variant="ghost"
              icon={<Refresh />}
              onClick={recheck}
              data-testid="btn-syscheck-recheck"
              css={`
                font-size: 13px;
                padding: 0.5rem 2.5rem;
                min-height: auto;
                & svg {
                  width: 16px;
                  height: 16px;
                }
              `}
            >
              Recheck
            </Button>
          </Stack>
        </SysChkContainer>
      </DialogContent>
      <DialogActions sx={{ flexDirection: 'column', gap: '0.75rem', pt: 1, pb: 2 }}>
        <Stack css="gap: 1rem;">
          <Tooltip
            content={
              isValidationInProgress
                ? VALIDATION_IN_PROGRESS_TOOLTIP_CONTENT
                : !shouldBypassSystemCheck
                  ? BYPASS_TOOLTIP_CONTENT
                  : null
            }
            placement="bottom"
          >
            <Button
              variant="light"
              disabled={isBypassDisabled}
              onClick={handleBypassClick}
              data-testid="btn-syscheck-bypass"
            >
              {shouldBypassSystemCheck ? 'Reset Bypass' : 'Bypass'}
            </Button>
          </Tooltip>
          <Button
            disabled={!(shouldBypassSystemCheck || isSystemValid)}
            onClick={() => setModalState(false)}
            data-testid="btn-syscheck-continue"
          >
            Continue
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};
